import * as React from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { playlistProgressSlice } from '../../reducers'
import { useSelector } from 'react-redux'

export const PlaylistProgressContext = React.createContext()

export const PlaylistProgressProvider = ({ children }) => {
  const [getPlaylistsProgress] = useEvent([
    playlistProgressSlice.eventBus.getPlaylistsProgress
  ])
  const playlistIds = useSelector(playlistProgressSlice.selectors.selectPlaylistIds)
  const requested = React.useRef()

  React.useEffect(() => {
    // A successful response empties the ids, so this is also the point where
    // the set that was asked about stops being the set in flight. Forgetting it
    // here is what lets the same playlist be asked about again later — marking
    // content as viewed re-arms the id of the playlist already open, and
    // remembering it forever swallowed that request and left the progress on
    // screen stale until a reload.
    if (!playlistIds.length) {
      requested.current = undefined
      return
    }

    // `playlistIds` is only cleared by a successful response, so a failed one
    // leaves this condition true and every later render fires the request
    // again — one failure became a stream of error toasts. Ask once per set.
    const key = playlistIds.join(',')
    if (requested.current === key) return
    requested.current = key

    getPlaylistsProgress.trigger({ playlistIds })
  }, [getPlaylistsProgress, playlistIds])

  const playlistProgressData = useSelector(playlistProgressSlice.selectors.selectPlaylistProgressData)

  const isMarkedAsRead = (playlistId, contentId) => {
    const playlist = playlistProgressData.find(playlist => playlist.playlistId === playlistId)
    if (!playlist?.read) return false
    return playlist.read.includes(contentId) || false
  }

  const isComplete = (playlistId) => {
    const playlist = playlistProgressData.find(playlist => playlist.playlistId === playlistId)
    return Math.round(playlist?.percent) === 100
  }

  const getProgress = (playlistId) => {
    const playlist = playlistProgressData.find(playlist => playlist.playlistId === playlistId)
    if (!playlist) return 0
    let percent = playlist.percent
    percent = (!percent || isNaN(percent)) ? 0 : percent
    percent = Math.round(percent)
    return percent
  }

  return (
    <PlaylistProgressContext.Provider value={{ isMarkedAsRead, getProgress, playlistProgressData, isComplete }}>
      {children}
    </PlaylistProgressContext.Provider>
  )
}

export const usePlaylistContext = () => {
  const context = React.useContext(PlaylistProgressContext)
  if (!context) {
    throw new Error(
      'usePlaylistContext must be used within the scope of PlaylistProgressProvider'
    )
  }
  return context
}
